import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "stepTile", "stepLabel", "stepCheck", "backButton", "nextButton", "submitButton", "cancelLink"]
  static values = {
    currentStep: { type: Number, default: 1 },
    maxReachedStep: { type: Number, default: 1 }
  }

  connect() {
    this.render()
    this.bindValidationListeners()
    this.startMutationObserver()
    this.validate()
  }

  disconnect() {
    if (this.mutationObserver) this.mutationObserver.disconnect()
  }

  bindValidationListeners() {
    this.validateHandler = () => this.validate()
    this.element.addEventListener("input", this.validateHandler)
    this.element.addEventListener("change", this.validateHandler)
  }

  startMutationObserver() {
    this.mutationObserver = new MutationObserver(() => this.validate())
    this.mutationObserver.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["value", "class"]
    })
  }

  validate() {
    const valid = this.isCurrentStepValid()
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = !valid
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = !valid
  }

  isCurrentStepValid() {
    switch (this.currentStepValue) {
      case 1: return this.hasAnyPhoto()
      case 2: return this.hasAnyComponent()
      case 3: return this.hasTitle()
      default: return false
    }
  }

  hasTitle() {
    const field = this.element.querySelector("input[name='room[title]']")
    return !!(field && field.value.trim().length > 0)
  }

  hasAnyComponent() {
    const wrappers = this.element.querySelectorAll(".nested-form-wrapper")
    const active = Array.from(wrappers).filter(wrapper => {
      if (wrapper.classList.contains("hidden")) return false
      const destroy = wrapper.querySelector("input[name*='_destroy']")
      if (destroy && (destroy.value === "1" || destroy.value === "true")) return false
      return true
    })
    if (active.length === 0) return false
    return active.every(wrapper => {
      const id = wrapper.querySelector("input[name*='component_id']")?.value
      const name = wrapper.querySelector("input[name*='component_name']")?.value
      const category = wrapper.querySelector("[name*='category_id']")?.value
      if (id) return true
      return !!(name && category)
    })
  }

  hasAnyPhoto() {
    const imageInputs = this.element.querySelectorAll("input[name^='room[photos_attributes]'][name$='[image]']")
    return Array.from(imageInputs).some(input => {
      if (!input.value) return false
      const wrapper = input.closest("[data-existing-photo], [data-sortable-item]") || input.parentElement
      if (wrapper?.classList.contains("hidden")) return false
      const destroyField = wrapper?.querySelector("[data-destroy-field]")
      return !destroyField || destroyField.value !== "true"
    })
  }

  render() {
    this.renderPanels()
    this.renderStepper()
    this.renderFooter()
  }

  renderFooter() {
    const step = this.currentStepValue
    const isFirst = step === 1
    const isLast = step === 3

    if (this.hasBackButtonTarget) this.backButtonTarget.classList.toggle("hidden", isFirst)
    if (this.hasCancelLinkTarget) this.cancelLinkTarget.classList.toggle("hidden", !isFirst)

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", isLast)
      if (step === 1) {
        this.nextButtonTarget.textContent = this.nextButtonTarget.dataset.nextLabelComponents
      } else if (step === 2) {
        this.nextButtonTarget.textContent = this.nextButtonTarget.dataset.nextLabelDetails
      }
    }

    if (this.hasSubmitButtonTarget) this.submitButtonTarget.classList.toggle("hidden", !isLast)
  }

  renderPanels() {
    this.panelTargets.forEach(panel => {
      const step = Number(panel.dataset.step)
      panel.classList.toggle("hidden", step !== this.currentStepValue)
    })
  }

  renderStepper() {
    this.stepTileTargets.forEach(tile => {
      const step = Number(tile.dataset.step)
      const isActive = step === this.currentStepValue
      const isCompleted = step < this.currentStepValue
      const isVisited = step <= this.maxReachedStepValue

      tile.classList.toggle("bg-text-primary", isActive)
      tile.classList.toggle("text-bg-base", isActive)
      tile.classList.toggle("font-semibold", isActive)

      tile.classList.toggle("bg-bg-raised", isCompleted)
      tile.classList.toggle("border-l-2", isCompleted)
      tile.classList.toggle("border-l-text-primary", isCompleted)

      tile.disabled = !isVisited

      const check = tile.querySelector("[data-room-wizard-target='stepCheck']")
      if (check) check.classList.toggle("hidden", !isCompleted)
    })
  }

  next() {
    if (this.currentStepValue >= 3) return
    if (!this.isCurrentStepValid()) return
    this.currentStepValue = this.currentStepValue + 1
    this.maxReachedStepValue = Math.max(this.maxReachedStepValue, this.currentStepValue)
    this.render()
    this.validate()
  }

  back() {
    if (this.currentStepValue <= 1) return
    this.currentStepValue = this.currentStepValue - 1
    this.render()
    this.validate()
  }

  jumpTo(event) {
    const target = Number(event.currentTarget.dataset.step)
    if (target > this.maxReachedStepValue) return
    this.currentStepValue = target
    this.render()
    this.validate()
  }
}
